import { FC, useEffect, useRef } from 'react';

import anime from 'animejs';

import styles from './Skills.module.css';
import {
  animations,
  attachAnimations,
  interactiveStyle,
  TriggerEvent,
  createFloatingAnimation,
} from '../../utils/interactions';
import Decorations from '../Decorations/Decorations';
import Icon from '../Icon/Icon';
import ScrollPrompt from '../ScrollPrompt/ScrollPrompt';

interface SkillCategory {
  name: string;
  icon: string;
  skills: Array<{
    name: string;
    icon: string;
  }>;
}

const skillCategories: SkillCategory[] = [
  {
    name: 'Mobile',
    icon: 'fluent:phone-mobile-24-filled',
    skills: [
      { name: 'Android', icon: 'logos:android-icon' },
      { name: 'iOS', icon: 'logos:apple' },
    ],
  },
  {
    name: 'Languages',
    icon: 'mdi:code-braces',
    skills: [
      { name: 'Kotlin', icon: 'logos:kotlin' },
      { name: 'Swift', icon: 'logos:swift' },
    ],
  },
  {
    name: 'Design',
    icon: 'mdi:palette-outline',
    skills: [
      { name: 'UI/UX', icon: 'mdi:palette-outline' },
      { name: 'Figma', icon: 'logos:figma' },
    ],
  },
  {
    name: 'Tools',
    icon: 'mdi:tools',
    skills: [
      { name: 'Git', icon: 'logos:git-icon' },
      { name: 'Gradle', icon: 'logos:gradle' },
    ],
  },
];

const Skills: FC = () => {
  const categoriesRef = useRef<HTMLUListElement>(null);
  const categoryIconsRef = useRef<HTMLDivElement[]>([]);
  const skillIconsRef = useRef<HTMLDivElement[]>([]);

  useEffect(() => {
    if (!categoriesRef.current) return;

    // Initial entrance animations
    const timeline = anime.timeline({
      easing: 'easeOutExpo',
    });

    timeline
      .add({
        targets: `.${styles.category}`,
        opacity: [0, 1],
        translateY: [20, 0],
        duration: 600,
        delay: anime.stagger(150),
      })
      .add(
        {
          targets: `.${styles.categoryIcon}`,
          opacity: [0, 1],
          scale: [0.5, 1],
          duration: 600,
          delay: anime.stagger(150),
        },
        '-=400'
      )
      .add(
        {
          targets: `.${styles.categoryTitle}`,
          opacity: [0, 1],
          translateY: [10, 0],
          duration: 500,
          delay: anime.stagger(100),
        },
        '-=400'
      )
      .add(
        {
          targets: `.${styles.skillItem}`,
          opacity: [0, 1],
          scale: [0.9, 1],
          translateX: () => {
            return [anime.random(-20, 20), 0];
          },
          translateY: () => {
            return [anime.random(-15, 15), 0];
          },
          duration: 800,
          delay: anime.stagger(40, {
            grid: [2, 2],
            from: 'center',
          }),
          easing: 'easeOutElastic(1, 0.6)',
        },
        '-=300'
      );

    // Setup continuous floating animations for category icons
    const floatingCleanups = categoryIconsRef.current.map((iconWrapper, index) =>
      createFloatingAnimation(iconWrapper, index * 300)
    );

    // Interactive animations
    const triggers: TriggerEvent[] = ['hover', 'touch'];
    const interactiveCleanup = attachAnimations([
      ...categoryIconsRef.current.map(iconWrapper => ({
        element: iconWrapper,
        animation: animations.gentleBounce(iconWrapper),
        triggers,
      })),
      ...skillIconsRef.current.map(iconWrapper => ({
        element: iconWrapper,
        animation: animations.rock(iconWrapper),
        triggers,
      })),
    ]);

    return () => {
      interactiveCleanup();
      floatingCleanups.forEach(cleanup => cleanup());
    };
  }, []);

  return (
    <section id="skills" className={styles.skills} aria-labelledby="skills-title">
      <h2 id="skills-title" className={styles.title}>
        <span>Technical Skills</span>
      </h2>

      <Decorations leftIcon="mdi:code-braces" rightIcon="mdi:palette-swatch" aria-hidden="true" />

      <div className={styles.container}>
        <ul className={styles.categories} ref={categoriesRef}>
          {skillCategories.map((category, categoryIndex) => (
            <li key={category.name} className={styles.category}>
              <div className={styles.categoryHeader}>
                <div
                  ref={el => {
                    if (el) categoryIconsRef.current[categoryIndex] = el;
                  }}
                  style={interactiveStyle}
                >
                  <Icon
                    icon={category.icon}
                    className={styles.categoryIcon}
                    size="lg"
                    aria-hidden={true}
                    aria-label={`${category.name} Category Icon`}
                  />
                </div>
                <h3
                  className={styles.categoryTitle}
                  id={`${category.name.toLowerCase().replace(/\s+/g, '-')}-title`}
                >
                  {category.name}
                </h3>
              </div>
              <ul
                className={styles.skillsList}
                aria-labelledby={`${category.name.toLowerCase().replace(/\s+/g, '-')}-title`}
              >
                {category.skills.map((skill, skillIndex) => (
                  <li key={skill.name} className={styles.skillItem}>
                    <div
                      ref={el => {
                        if (el) skillIconsRef.current[categoryIndex * 2 + skillIndex] = el;
                      }}
                      style={interactiveStyle}
                    >
                      <Icon
                        icon={skill.icon}
                        className={styles.skillIcon}
                        size="md"
                        aria-hidden={true}
                        aria-label={`${skill.name} Icon`}
                      />
                    </div>
                    <span className={styles.skillName}>{skill.name}</span>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </div>

      <div className={styles.scrollPromptWrapper}>
        <ScrollPrompt
          targetId="achievements"
          label="Achievements"
          showIcon={true}
          aria-label="Scroll to achievements section"
        />
      </div>
    </section>
  );
};

export default Skills;
